import { useLayoutEffect, useRef } from "react";

const TO_RAIL_DURATION = 860;
const TO_NAV_DURATION = 800;
const MAX_PARTICLES = 7200;
const DUST_COLORS = ["#f4f8ff", "#b9f3ff", "#d4c9ff", "#eef3fa"] as const;

type Point = { x: number; y: number };
type LayoutRect = Point & { height: number; width: number };
type PixelTile = Point & { size: number };
type RasterSnapshot = {
  bounds: LayoutRect;
  canvas: HTMLCanvasElement;
  tiles: PixelTile[];
};
type PixelParticle = {
  arrival: number;
  control: Point;
  delay: number;
  drift: number;
  glow: (typeof DUST_COLORS)[number];
  end: { point: Point; snapshot: RasterSnapshot; tile: PixelTile };
  start: { point: Point; snapshot: RasterSnapshot; tile: PixelTile };
};

function randomBetween(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function smootherstep(progress: number) {
  return progress * progress * progress * (progress * (progress * 6 - 15) + 10);
}

function shuffle<T>(values: T[]) {
  for (let index = values.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [values[index], values[swapIndex]] = [values[swapIndex] as T, values[index] as T];
  }
  return values;
}

function localRectWithin(element: HTMLElement, ancestor: HTMLElement): LayoutRect {
  const elementRect = element.getBoundingClientRect();
  const ancestorRect = ancestor.getBoundingClientRect();
  return {
    x: elementRect.left - ancestorRect.left,
    y: elementRect.top - ancestorRect.top,
    width: elementRect.width,
    height: elementRect.height,
  };
}

function viewportRect(element: HTMLElement): LayoutRect {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left,
    y: rect.top,
    width: rect.width,
    height: rect.height,
  };
}

function cloneWithComputedStyles(source: HTMLElement) {
  const clone = source.cloneNode(true) as HTMLElement;
  const sourceNodes = [source, ...Array.from(source.querySelectorAll<HTMLElement>("*"))];
  const cloneNodes = [clone, ...Array.from(clone.querySelectorAll<HTMLElement>("*"))];

  sourceNodes.forEach((sourceNode, index) => {
    const cloneNode = cloneNodes[index];
    if (!cloneNode) return;
    const styles = window.getComputedStyle(sourceNode);
    for (let propertyIndex = 0; propertyIndex < styles.length; propertyIndex += 1) {
      const property = styles.item(propertyIndex);
      cloneNode.style.setProperty(
        property,
        styles.getPropertyValue(property),
        styles.getPropertyPriority(property),
      );
    }
    cloneNode.style.animation = "none";
    cloneNode.style.transition = "none";
  });

  clone.removeAttribute("inert");
  clone.style.position = "relative";
  clone.style.inset = "auto";
  clone.style.margin = "0";
  clone.style.transform = "none";
  clone.style.filter = "none";
  clone.style.opacity = "1";
  clone.style.visibility = "visible";
  clone.style.pointerEvents = "none";
  clone.style.width = `${source.offsetWidth}px`;
  clone.style.height = `${source.offsetHeight}px`;

  // Keep the rasterized endpoint geometrically identical to the live nav.
  // SVG foreignObject can otherwise reflow multi-word flex items while its
  // fonts settle, which makes labels appear to disappear and then respawn.
  clone.style.flexWrap = "nowrap";
  clone
    .querySelectorAll<HTMLElement>(
      ".logo, .nav-shell__links, .nav-shell__links > a, .nav-shell__access",
    )
    .forEach((node) => {
      node.style.flexShrink = "0";
      node.style.whiteSpace = "nowrap";
    });

  return clone;
}

function imageFromSvg(svg: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not rasterize the navigation morph source."));
    };
    image.src = url;
  });
}

function createTiles(canvas: HTMLCanvasElement, step: number, regions: LayoutRect[]) {
  const tiles: PixelTile[] = [];
  const seen = new Set<string>();

  regions.forEach((region) => {
    const startX = Math.max(0, Math.floor(region.x / step) * step);
    const startY = Math.max(0, Math.floor(region.y / step) * step);
    const endX = Math.min(canvas.width, Math.ceil((region.x + region.width) / step) * step);
    const endY = Math.min(canvas.height, Math.ceil((region.y + region.height) / step) * step);

    for (let y = startY; y < endY; y += step) {
      for (let x = startX; x < endX; x += step) {
        const key = `${x}:${y}`;
        if (seen.has(key)) continue;
        seen.add(key);
        tiles.push({ x, y, size: step });
      }
    }
  });

  return tiles.length > MAX_PARTICLES
    ? shuffle(tiles).slice(0, MAX_PARTICLES)
    : shuffle(tiles);
}

async function captureElement(
  element: HTMLElement,
  bounds: LayoutRect,
  padding: number,
  visibleSelector?: string,
) {
  const clone = cloneWithComputedStyles(element);
  const width = Math.ceil(bounds.width + padding * 2);
  const height = Math.ceil(bounds.height + padding * 2);
  const serialized = new XMLSerializer().serializeToString(clone);
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
    `<foreignObject width="${width}" height="${height}">`,
    `<div xmlns="http://www.w3.org/1999/xhtml" style="position:relative;width:${width}px;height:${height}px;overflow:visible;">`,
    `<div style="position:absolute;left:${padding}px;top:${padding}px;">${serialized}</div>`,
    "</div></foreignObject></svg>",
  ].join("");
  const image = await imageFromSvg(svg);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Could not create the navigation morph raster.");
  context.drawImage(image, 0, 0);

  const localRoot: LayoutRect = { x: 0, y: 0, width: element.offsetWidth, height: element.offsetHeight };
  const visibleElements = visibleSelector
    ? Array.from(element.querySelectorAll<HTMLElement>(visibleSelector))
    : [element];
  const regions = visibleElements.map((visibleElement) => {
    const region = visibleElement === element
      ? localRoot
      : localRectWithin(visibleElement, element);
    return {
      x: padding + region.x,
      y: padding + region.y,
      width: region.width,
      height: region.height,
    };
  });

  const step = bounds.width > 300 ? 4 : 2;
  const tiles = createTiles(canvas, step, regions);
  const snapshot: RasterSnapshot = {
    canvas,
    bounds: {
      x: bounds.x - padding,
      y: bounds.y - padding,
      width,
      height,
    },
    tiles: tiles.length > 0
      ? tiles
      : createTiles(canvas, step, [{ ...localRoot, x: padding, y: padding }]),
  };
  return snapshot;
}

function particleEndpoint(tile: PixelTile, snapshot: RasterSnapshot) {
  return {
    tile,
    snapshot,
    point: {
      x: snapshot.bounds.x + tile.x + tile.size / 2,
      y: snapshot.bounds.y + tile.y + tile.size / 2,
    },
  };
}

function createParticles(startSnapshot: RasterSnapshot, endSnapshot: RasterSnapshot) {
  const particleCount = Math.max(startSnapshot.tiles.length, endSnapshot.tiles.length);

  return Array.from({ length: particleCount }, (_, index): PixelParticle => {
    const start = particleEndpoint(
      startSnapshot.tiles[index % startSnapshot.tiles.length] as PixelTile,
      startSnapshot,
    );
    const end = particleEndpoint(
      endSnapshot.tiles[index % endSnapshot.tiles.length] as PixelTile,
      endSnapshot,
    );
    const delay = Math.random() * 0.14;
    return {
      start,
      end,
      arrival: Math.min(0.97, delay + randomBetween(0.64, 0.82)),
      control: {
        x: (start.point.x + end.point.x) / 2 + randomBetween(-80, 28),
        y: (start.point.y + end.point.y) / 2 + randomBetween(-92, 36),
      },
      delay,
      drift: randomBetween(-18, 18),
      glow: DUST_COLORS[Math.floor(Math.random() * DUST_COLORS.length)] as (typeof DUST_COLORS)[number],
    };
  });
}

function curvePoint(particle: PixelParticle, progress: number): Point {
  const inverse = 1 - progress;
  const x =
    inverse * inverse * particle.start.point.x +
    2 * inverse * progress * particle.control.x +
    progress * progress * particle.end.point.x;
  const y =
    inverse * inverse * particle.start.point.y +
    2 * inverse * progress * particle.control.y +
    progress * progress * particle.end.point.y;
  const dx = particle.end.point.x - particle.start.point.x;
  const dy = particle.end.point.y - particle.start.point.y;
  const distance = Math.max(1, Math.hypot(dx, dy));
  const drift = Math.sin(progress * Math.PI) * particle.drift;
  return {
    x: x + (-dy / distance) * drift,
    y: y + (dx / distance) * drift,
  };
}

function drawSnapshot(context: CanvasRenderingContext2D, snapshot: RasterSnapshot) {
  context.drawImage(snapshot.canvas, snapshot.bounds.x, snapshot.bounds.y);
}

function mix(start: number, end: number, progress: number) {
  return start + (end - start) * progress;
}

function drawTile(
  context: CanvasRenderingContext2D,
  endpoint: PixelParticle["start"],
  point: Point,
  size: number,
  alpha: number,
) {
  const sourceWidth = Math.min(endpoint.tile.size, endpoint.snapshot.canvas.width - endpoint.tile.x);
  const sourceHeight = Math.min(endpoint.tile.size, endpoint.snapshot.canvas.height - endpoint.tile.y);
  if (sourceWidth <= 0 || sourceHeight <= 0 || alpha <= 0) return;
  context.globalAlpha = alpha;
  context.drawImage(
    endpoint.snapshot.canvas,
    endpoint.tile.x,
    endpoint.tile.y,
    sourceWidth,
    sourceHeight,
    point.x - size / 2,
    point.y - size / 2,
    size,
    size,
  );
}

export default function NavigationDustMorph({ railVisible }: { railVisible: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previousVisibility = useRef(railVisible);
  const snapshots = useRef<{ nav?: RasterSnapshot; rail?: RasterSnapshot }>({});

  useLayoutEffect(() => {
    const motionAllowed = window.matchMedia(
      "(min-width: 821px) and (prefers-reduced-motion: no-preference)",
    );
    if (!motionAllowed.matches) return;

    let cancelled = false;
    const prepareSnapshots = async () => {
      await document.fonts.ready;
      const nav = document.querySelector<HTMLElement>(".nav-shell");
      const rail = document.querySelector<HTMLElement>(".chapter-rail__assembly");
      if (!nav || !rail || cancelled) return;
      const [navSnapshot, railSnapshot] = await Promise.all([
        captureElement(nav, viewportRect(nav), 24),
        captureElement(
          rail,
          viewportRect(rail),
          18,
          ".chapter-rail__brand, .chapter-scrubber__tick",
        ),
      ]);
      if (cancelled) return;
      snapshots.current = { nav: navSnapshot, rail: railSnapshot };
    };

    void prepareSnapshots().catch((error: unknown) => {
      console.error("Failed to prepare the navigation pixel morph.", error);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    const wasVisible = previousVisibility.current;
    previousVisibility.current = railVisible;
    if (wasVisible === railVisible) return;

    const motionAllowed = window.matchMedia(
      "(min-width: 821px) and (prefers-reduced-motion: no-preference)",
    );
    const canvas = canvasRef.current;
    const nav = document.querySelector<HTMLElement>(".nav-shell");
    const rail = document.querySelector<HTMLElement>(".chapter-rail__assembly");
    if (!motionAllowed.matches || !canvas || !nav || !rail) return;

    const direction = railVisible ? "to-rail" : "to-nav";
    const duration = railVisible ? TO_RAIL_DURATION : TO_NAV_DURATION;
    const context = canvas.getContext("2d");
    if (!context) return;

    let cancelled = false;
    let animationFrame = 0;
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(window.innerWidth * pixelRatio);
    canvas.height = Math.round(window.innerHeight * pixelRatio);
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    canvas.classList.add("is-active");
    document.documentElement.dataset.navigationMorph = direction;
    document.documentElement.style.setProperty("--navigation-morph-handoff", "0");

    const cachedSource = railVisible ? snapshots.current.nav : snapshots.current.rail;
    if (cachedSource) drawSnapshot(context, cachedSource);

    const animate = async () => {
      const [navSnapshot, railSnapshot] = await Promise.all([
        captureElement(nav, viewportRect(nav), 24),
        captureElement(
          rail,
          viewportRect(rail),
          18,
          ".chapter-rail__brand, .chapter-scrubber__tick",
        ),
      ]);
      if (cancelled) return;
      snapshots.current = { nav: navSnapshot, rail: railSnapshot };

      const startSnapshot = railVisible ? navSnapshot : railSnapshot;
      const endSnapshot = railVisible ? railSnapshot : navSnapshot;
      const particles = createParticles(startSnapshot, endSnapshot);
      const startedAt = performance.now();

      const draw = (now: number) => {
        const elapsed = Math.min(1, (now - startedAt) / duration);
        const handoff = smootherstep(
          Math.min(1, Math.max(0, (elapsed - 0.78) / 0.2)),
        );
        const particleOpacity = 1 - handoff;
        document.documentElement.style.setProperty(
          "--navigation-morph-handoff",
          handoff.toFixed(4),
        );
        context.clearRect(0, 0, window.innerWidth, window.innerHeight);

        particles.forEach((particle) => {
          const localProgress = Math.min(
            1,
            Math.max(0, (elapsed - particle.delay) / (particle.arrival - particle.delay)),
          );
          const eased = smootherstep(localProgress);
          const point = curvePoint(particle, eased);
          const materialProgress = smootherstep(
            Math.min(1, Math.max(0, (localProgress - 0.4) / 0.42)),
          );
          const middleShrink = 1 - Math.sin(eased * Math.PI) * 0.34;
          const size = mix(particle.start.tile.size, particle.end.tile.size, eased) * middleShrink;
          drawTile(
            context,
            particle.start,
            point,
            size,
            (1 - materialProgress) * particleOpacity,
          );
          drawTile(
            context,
            particle.end,
            point,
            size,
            materialProgress * particleOpacity,
          );

          const dustBrightness = Math.sin(localProgress * Math.PI) * 0.34 * particleOpacity;
          if (dustBrightness > 0.01) {
            const sparkSize = Math.max(1.35, size * 0.72);
            context.globalAlpha = dustBrightness;
            context.fillStyle = particle.glow;
            context.fillRect(
              point.x - sparkSize / 2,
              point.y - sparkSize / 2,
              sparkSize,
              sparkSize,
            );
          }
        });

        context.globalAlpha = 1;

        if (elapsed < 1) {
          animationFrame = window.requestAnimationFrame(draw);
          return;
        }

        animationFrame = window.requestAnimationFrame(() => {
          canvas.classList.remove("is-active");
          context.clearRect(0, 0, window.innerWidth, window.innerHeight);
          if (document.documentElement.dataset.navigationMorph === direction) {
            delete document.documentElement.dataset.navigationMorph;
          }
          document.documentElement.style.removeProperty("--navigation-morph-handoff");
        });
      };

      animationFrame = window.requestAnimationFrame(draw);
    };

    void animate().catch((error: unknown) => {
      console.error("Failed to run the navigation pixel morph.", error);
      canvas.classList.remove("is-active");
      if (document.documentElement.dataset.navigationMorph === direction) {
        delete document.documentElement.dataset.navigationMorph;
      }
      document.documentElement.style.removeProperty("--navigation-morph-handoff");
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(animationFrame);
      canvas.classList.remove("is-active");
      context.clearRect(0, 0, window.innerWidth, window.innerHeight);
      if (document.documentElement.dataset.navigationMorph === direction) {
        delete document.documentElement.dataset.navigationMorph;
      }
      document.documentElement.style.removeProperty("--navigation-morph-handoff");
    };
  }, [railVisible]);

  return <canvas aria-hidden="true" className="navigation-dust-morph" ref={canvasRef} />;
}
